// File: src/pages/InstallCustomScript.jsx

import React, { useEffect, useContext, useState, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { Page } from '@shopify/polaris';
import { AuthContext } from '../context/AuthContext';
import OnboardingBanner from '../components/OnboardingBanner';
import FlashCardCongrats from '../components/ui/trackerInstalled';
import styles from './IntegrationsPage.module.css';
import './InstallCustomScript.css';

//const TRACKER = process.env.REACT_APP_API_URL || 'https://api.baobrain.com';
const TRACKER = 'https://api.baobrain.com';
// Icons
const CheckIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <polyline points="20 6 9 17 4 12" />
  </svg>
);

const GoogleIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24">
    <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
    <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
    <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
    <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
  </svg>
);



const ArrowLeftIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M19 12H5M12 19l-7-7 7-7"/>
  </svg>
);

const ArrowRightIcon = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M5 12h14M12 5l7 7-7 7"/>
  </svg>
);

const CopyIcon = () => (
  <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
    <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
  </svg>
);

const AnalyticsIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <path d="M3 3v18h18" />
    <path d="M18 17V9" />
    <path d="M13 17V5" />
    <path d="M8 17v-3" />
  </svg>
);

const CodeIcon = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
    <polyline points="16 18 22 12 16 6"/>
    <polyline points="8 6 2 12 8 18"/>
  </svg>
);

export default function InstallCustomScript() {
  const navigate = useNavigate();
  const { search, state } = useLocation();
  const qs = new URLSearchParams(search);

  // Rehydrate `site` from location.state OR query-string
  const stateSite = state?.site
    ? {
        id: state.site.id,
        token: state.site.token,
        shopDomain: state.site.url
      }
    : null;

  const urlSite = qs.get('siteId')
    ? {
        id: Number(qs.get('siteId')),
        token: qs.get('siteToken'),
        shopDomain: qs.get('shopDomain')
      }
    : null;

  const site = stateSite || urlSite;

  const { token, setToken } = useContext(AuthContext);
  const [didReadToken, setDidReadToken] = useState(false);

  // GA4-related state hooks
  const [status, setStatus] = useState({ loading: true, connected: false });
  const [propsList, setPropsList] = useState([]);
  const [propsLoading, setPropsLoading] = useState(false);
  const [propId, setPropId] = useState('');
  const [savedPropId, setSavedPropId] = useState('');
  const [processing, setProcessing] = useState(false);
  const [disconnecting, setDisconnecting] = useState(false);
  const [generalError, setGeneralError] = useState('');
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [copied, setCopied] = useState(false);

  // Carousel slide control (0 = welcome, 1 = GA4, 2 = copy script)
  const [slideIndex, setSlideIndex] = useState(0);

  // Inline confirmation toast — brief mid-step success messages between slides.
  const [toast, setToast] = useState(null); // { msg: string, key: number }
  useEffect(() => {
    if (!toast) return;
    const t = setTimeout(() => setToast(null), 3500);
    return () => clearTimeout(t);
  }, [toast]);

  // Fire toast when GA4 connection completes
  const prevConnectedRef = React.useRef(false);
  useEffect(() => {
    if (status.loading) return;
    if (!prevConnectedRef.current && status.connected) {
      setToast({ msg: 'Google account connected successfully', key: Date.now() });
    }
    prevConnectedRef.current = status.connected;
  }, [status.connected, status.loading]);

  // Fire toast when GA4 property is saved
  const prevSavedRef = React.useRef('');
  useEffect(() => {
    if (savedPropId && savedPropId !== prevSavedRef.current && prevSavedRef.current !== '') {
      setToast({ msg: 'Analytics property saved', key: Date.now() });
    } else if (savedPropId && prevSavedRef.current === '') {
      // Don't fire on initial load
    }
    prevSavedRef.current = savedPropId;
  }, [savedPropId]);

  // Fire toast when user copies the script
  const prevCopiedRef = React.useRef(false);
  useEffect(() => {
    if (!prevCopiedRef.current && copied) {
      setToast({ msg: 'Script copied — paste it into your site\'s <head>', key: Date.now() });
    }
    prevCopiedRef.current = copied;
  }, [copied]);

  // ============================================
  //  ALL ORIGINAL useEffects - PRESERVED
  // ============================================

  // Handle JWT from Google OAuth redirect and strip it from the URL
  useEffect(() => {
    if (didReadToken) return;
    const params = new URLSearchParams(search);
    const t = params.get('token');
    if (t) {
      setToken(t);
      // Mark OAuth return
      sessionStorage.setItem('custom_oauth_return', 'true');
      
      params.delete('token');
      const newQs = params.toString();

      window.history.replaceState(
        window.history.state,
        '',
        `/install-custom-script${newQs ? `?${newQs}` : ''}`
      );
    }
    setDidReadToken(true);
  }, [search, didReadToken, setToken]);

  // After status loads, check if we should skip to step 2
  useEffect(() => {
    if (status.loading) return;
    
    const oauthReturn = sessionStorage.getItem('custom_oauth_return');
    
    if (oauthReturn) {
      sessionStorage.removeItem('custom_oauth_return');
      setSlideIndex(1);
    } else if (status.connected) {
      setSlideIndex(1);
    }
  }, [status.loading, status.connected]);

  // If after rehydration there is no site, navigate to dashboard
  useEffect(() => {
    if (!site) {
      navigate('/dashboard');
    }
  }, [site, navigate]);

  // 1. Fetch integration status
  useEffect(() => {
    async function fetchStatus() {
      if (!token) {
        return setStatus({ loading: false, connected: false });
      }
      try {
        const res = await fetch(`${TRACKER}/integrations/status`, { headers: { Authorization: `Bearer ${token}` } });
        const json = await res.json();
        setStatus({ loading: false, connected: json.connected });
      } catch {
        setStatus({ loading: false, connected: false });
        setGeneralError('Failed to fetch integration status.');
      }
    }
    fetchStatus();
  }, [token]);

  // 2. When connected, load savedPropId
  useEffect(() => {
    if (!status.connected) return;
    fetch(`${TRACKER}/integrations/property`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.json())
      .then(j => {
        if (j.propertyId) {
          setSavedPropId(j.propertyId);
        }
      })
      .catch(() => {});
  }, [status.connected, token]);

  // 3. When connected, load propsList
  useEffect(() => {
    if (!status.connected) return;
    setPropsLoading(true);
    fetch(`${TRACKER}/integrations/ga4-properties`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => {
        if (!r.ok) throw new Error();
        return r.json();
      })
      .then(list => {
        setPropsList(list);
        if (list.length) setPropId((current) => current || list[0].measurementId);
      })
      .catch(e => setGeneralError(`Error loading GA4 properties: ${e.message}`))
      .finally(() => setPropsLoading(false));
  }, [status.connected, token]);

  // Effect to correct numeric ID to G- prefixed ID
  useEffect(() => {
    if (savedPropId && !savedPropId.startsWith('G-') && propsList.length > 0) {
      const correctProp = propsList.find(p =>
        p.propertyId === savedPropId ||
        p.measurementId === savedPropId
      );
      if (correctProp && correctProp.measurementId) {
        setSavedPropId(correctProp.measurementId);
      }
    }
  }, [savedPropId, propsList]);

  // ============================================
  //  ALL ORIGINAL HANDLERS - PRESERVED
  // ============================================

  const handleConnectGoogle = () => {
    const nextPath =
      `/install-custom-script?` +
      `siteId=${site.id}` +
      `&siteToken=${encodeURIComponent(site.token)}` +
      `&shopDomain=${encodeURIComponent(site.shopDomain)}`;
    window.location.href =
      `${TRACKER}/auth/google?` +
      `next=${encodeURIComponent(nextPath)}` +
      `&linkToken=${encodeURIComponent(token)}`;
  };

  const handleSaveGA4 = useCallback(async () => {
    setProcessing(true);
    setGeneralError('');
    try {
      const res = await fetch(`${TRACKER}/integrations/property`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ propertyId: propId }),
      });
      if (!res.ok) throw new Error(await res.text());
      setSavedPropId(propId);
    } catch (e) {
      setGeneralError(`Save failed: ${e.message}`);
    } finally {
      setProcessing(false);
    }
  }, [token, propId]);

  const handleDisconnectGA4 = useCallback(async () => {
    if (!window.confirm('Disconnect Google Analytics?')) return;
    setDisconnecting(true);
    setGeneralError('');

    try {
      const res = await fetch(
        `${TRACKER}/integrations/disconnect-ga4`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify({ shop: site.shopDomain }),
        }
      );
      if (!res.ok) {
        throw new Error(await res.text());
      }

      setStatus({ loading: false, connected: false });
      setSavedPropId('');
      setPropsList([]);
      setPropId('');

      const nextPath =
        `/install-custom-script?` +
        `siteId=${site.id}` +
        `&siteToken=${encodeURIComponent(site.token)}` +
        `&shopDomain=${encodeURIComponent(site.shopDomain)}`;
      window.location.href =
        `${TRACKER}/auth/google?` +
        `next=${encodeURIComponent(nextPath)}` +
        `&linkToken=${encodeURIComponent(token)}`;

    } catch (err) {
      setGeneralError(`Disconnect failed: ${err.message}`);
    } finally {
      setDisconnecting(false);
    }
  }, [token, site]);

  // Navigation
  const goNext = () => setSlideIndex((prev) => Math.min(prev + 1, 2));
  const goBack = () => setSlideIndex((prev) => Math.max(prev - 1, 0));

  // ============================================
  //  SCRIPT GENERATION - PRESERVED
  // ============================================

  if (!site) {
    return (
      <Page title="Set Up Website Tracking">
        <div className={styles.page}>
          <div className={styles.card}>
            <div className={styles.cardBody}>
              <p className={styles.statusText}>Loading...</p>
            </div>
          </div>
        </div>
      </Page>
    );
  }

  const ga4Script = savedPropId ? `
<script async src="https://www.googletagmanager.com/gtag/js?id=${savedPropId}"></script>
<script>
  window.dataLayer = window.dataLayer || [];
  function gtag(){dataLayer.push(arguments);}
  gtag('js', new Date());
  gtag('config','${savedPropId}');
</script>
  `.trim() : '';

  const trackerLoaderScript = `
<script
  src="${TRACKER}/shopify/tracker.js"
  data-site-id="${site.id}"
  data-site-token="${site.token}"
  data-host="${TRACKER}"
  defer
></script>
  `.trim();

  const sessionsLoaderScript = `
<script
  src="${TRACKER}/shopify/sessions.js"
  data-site-id="${site.id}"
  data-site-token="${site.token}"
  data-host="${TRACKER}"
  data-sessions-path="/static/sessions.bundle.js"
></script>
  `.trim();

  const allScripts = savedPropId
    ? `${ga4Script}\n\n${trackerLoaderScript}\n\n${sessionsLoaderScript}`
    : `${trackerLoaderScript}\n\n${sessionsLoaderScript}`;

  const handleCopy = () => {
    navigator.clipboard.writeText(allScripts).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }).catch(console.error);
  };

  // Derived state
  const isComplete = !!savedPropId;

  // Step Tracker - based on actual progress
  const StepTracker = () => {
    const getStepStatus = (stepNum) => {
      if (stepNum === 1) return 'complete'; // Install always done
      if (stepNum === 2) return status.connected ? 'complete' : (slideIndex >= 1 ? 'current' : 'pending');
      if (stepNum === 3) return isComplete ? 'complete' : (slideIndex >= 2 ? 'current' : 'pending');
      return 'pending';
    };

    return (
      <div className={styles.stepTracker}>
        {/* Step 1: Install */}
        <div className={styles.stepIndicator}>
          <div className={`${styles.stepIcon} ${styles.complete}`}>
            <CheckIcon />
          </div>
          <span className={`${styles.stepLabel} ${styles.complete}`}>Install</span>
        </div>

        <div className={styles.stepLine}>
          <div className={styles.stepLineProgress} style={{ width: status.connected ? '100%' : '0%' }} />
        </div>

        {/* Step 2: Connect */}
        <div className={styles.stepIndicator}>
          <div className={`${styles.stepIcon} ${styles[getStepStatus(2)]}`}>
            {status.connected ? <CheckIcon /> : <AnalyticsIcon />}
          </div>
          <span className={`${styles.stepLabel} ${styles[getStepStatus(2)]}`}>Connect</span>
        </div>

        <div className={styles.stepLine}>
          <div className={styles.stepLineProgress} style={{ width: isComplete ? '100%' : '0%' }} />
        </div>

        {/* Step 3: Copy Script */}
        <div className={styles.stepIndicator}>
          <div className={`${styles.stepIcon} ${styles[getStepStatus(3)]}`}>
            {isComplete ? <CheckIcon /> : <CodeIcon />}
          </div>
          <span className={`${styles.stepLabel} ${styles[getStepStatus(3)]}`}>Copy Script</span>
        </div>
      </div>
    );
  };

  // Card 1: Welcome/Onboarding
  const renderWelcomeSlide = () => (
    <div className={styles.slide}>
      <OnboardingBanner
        currentStep={slideIndex + 1}
        onNextStep={goNext}
        onDismiss={() => navigate('/dashboard')}
      />
    </div>
  );

  // Card 2: Connect Google + Select GA4 Property
  const renderGA4Slide = () => (
    <div className={styles.slide}>
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <h2>Add Google Analytics <span className={styles.optionalTag}>Optional</span></h2>
          <p>BaoBrain already tracks your site on its own. Linking GA4 layers in your historical Google data for deeper insights.</p>
        </div>

        <div className={styles.cardBody}>
          {status.loading ? (
            <>
              <div className={styles.loadingSpinner} />
              <p className={styles.statusText}>Loading…</p>
            </>
          ) : !status.connected ? (
            <>
              <p className={styles.helpText}>
                If you already use Google Analytics, link it now so BaoBrain can pull your GA4 data in alongside its own tracking.
              </p>
              <button
                className={styles.btnPrimary}
                onClick={handleConnectGoogle}
                disabled={!site.token}
              >
                <GoogleIcon /> Connect Google Account
              </button>
              <p className={styles.helpText}>
                Not using Google Analytics? Skip this step — BaoBrain's own tracking captures everything you need.
              </p>
            </>
          ) : propsLoading ? (
            <>
              <div className={styles.loadingSpinner} />
              <p className={styles.statusText}>Looking for your GA4 properties…</p>
              <p className={styles.helpText}>This usually takes a few seconds.</p>
            </>
          ) : (
            <>
              <div className={styles.connectedBadge}>
                <CheckIcon size={16} />
                <span>Google connected — GA4 data will layer in</span>
              </div>

              {propsList.length > 0 ? (
                <>
                  <div className={styles.selectGroup}>
                    <label htmlFor="ga4-property">Choose your Analytics property</label>
                    <select
                      id="ga4-property"
                      value={propId}
                      onChange={(e) => setPropId(e.target.value)}
                    >
                      {propsList.map((p) => (
                        <option key={p.measurementId} value={p.measurementId}>
                          {p.displayName}
                        </option>
                      ))}
                    </select>
                  </div>

                  <button
                    className={styles.btnPrimary}
                    onClick={handleSaveGA4}
                    disabled={!propId || propId === savedPropId || processing}
                  >
                    {processing ? 'Saving…' : savedPropId ? 'Update property' : 'Save & continue'}
                  </button>

                  <p className={styles.helpText}>
                    Don't see the right one?{' '}
                    <a href="https://analytics.google.com/" target="_blank" rel="noopener noreferrer">
                      Open Google Analytics
                    </a>
                  </p>
                </>
              ) : (
                <div className={styles.fallbackBox}>
                  <p className={styles.fallbackTitle}>No Analytics properties on this account</p>
                  <p className={styles.helpText}>
                    No problem — you can skip this step and still get full BaoBrain insights from our pixel.
                  </p>
                  <a
                    href="https://support.google.com/analytics/answer/9304153"
                    target="_blank"
                    rel="noopener noreferrer"
                    className={styles.fallbackLink}
                  >
                    Don't have Google Analytics? Set it up in 2 minutes →
                  </a>
                </div>
              )}

              <button className={styles.btnText} onClick={handleDisconnectGA4} disabled={disconnecting}>
                {disconnecting ? 'Switching…' : 'Use a different Google account'}
              </button>
            </>
          )}

          {generalError && <p className={styles.errorText}>{generalError}</p>}
        </div>

        <div className={styles.cardFooter}>
          <button className={styles.btnSecondary} onClick={goBack}>
            <ArrowLeftIcon /> Back
          </button>
          {status.connected && !propsLoading && propsList.length === 0 ? (
            <button className={styles.btnPrimary} onClick={goNext}>
              Skip for now <ArrowRightIcon />
            </button>
          ) : (
            <button className={styles.btnPrimary} onClick={goNext} disabled={propsLoading}>
              {propId ? 'Next' : 'Skip for now'} <ArrowRightIcon />
            </button>
          )}
        </div>
      </div>
    </div>
  );

  // Card 3: Copy Tracking Script
  const renderScriptSlide = () => (
    <div className={styles.slide}>
      <div className={styles.card}>
        <div className={styles.cardHeader}>
          <h2>Almost there — paste this snippet</h2>
          <p>Drop this into the &lt;head&gt; of your site and you'll unlock everything below</p>
        </div>

        <div className={styles.cardBody} style={{ alignItems: 'stretch' }}>
          <div className="code-block-wrapper">
            <pre>{allScripts}</pre>
            <button
              className="copy-btn"
              onClick={handleCopy}
            >
              <CopyIcon /> {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>

          <ul className={styles.unlockList}>
            <li><CheckIcon size={16} /> Real-time visitor &amp; session tracking</li>
            <li><CheckIcon size={16} /> Conversion funnel + drop-off detection</li>
            {savedPropId && <li><CheckIcon size={16} /> GA4 historical data layered in</li>}
            <li><CheckIcon size={16} /> Customer intelligence dashboards</li>
          </ul>

          <div className="install-steps">
            <p className={styles.helpText}><strong>What to do next:</strong></p>
            <p className={styles.helpText}>
              1. Copy the code above<br/>
              2. Paste it just before the closing &lt;/head&gt; tag in your site's HTML<br/>
              3. Save and reload — that's it
            </p>
          </div>

          {!savedPropId && (
            <div className={styles.inlineAlert}>
              <span className={styles.inlineAlertIcon}>!</span>
              <span>Google Analytics isn't connected. The pixel still works — but go back if you want to add GA4 too.</span>
            </div>
          )}
        </div>

        <div className={styles.cardFooter}>
          <button className={styles.btnSecondary} onClick={goBack}>
            <ArrowLeftIcon /> Back
          </button>
          <button
            className={styles.btnPrimary}
            onClick={() => setShowSuccessModal(true)}
          >
            I've added it
          </button>
        </div>
      </div>
    </div>
  );

  return (
    <div className="install-custom-script-page">
      <Page
        title="Set up your website"
        backAction={{ content: 'Back to Dashboard', onAction: () => navigate('/dashboard') }}
      >
        <div className={styles.page}>
          <StepTracker />

          {toast && (
            <div className={styles.toast} key={toast.key} role="status" aria-live="polite">
              <span className={styles.toastIcon}>
                <CheckIcon size={14} />
              </span>
              <span>{toast.msg}</span>
            </div>
          )}

          <div className={styles.carousel}>
            <div className={styles.track} style={{ transform: `translateX(-${slideIndex * 100}%)` }}>
              {renderWelcomeSlide()}
              {renderGA4Slide()}
              {renderScriptSlide()}
            </div>
          </div>

          <div className={styles.support}>
            Need help?{' '}
            <a href="https://app.baobrain.com/installation" target="_blank" rel="noopener noreferrer">Setup Guide</a>
            {' · '}
            <a href="/help" target="_blank" rel="noopener noreferrer">Contact Support</a>
          </div>
        </div>

        {/* Success Modal */}
        {showSuccessModal && (
          <div className={styles.modalOverlay} onClick={() => setShowSuccessModal(false)}>
            <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
              <FlashCardCongrats
                title="🎉 You've unlocked your insights"
                shopDomain={site.shopDomain || null}
                message={
                  <>
                    <p style={{ marginBottom: 12 }}>
                      Once that snippet is live on your site, BaoBrain will start watching every visitor. Here's what you just turned on:
                    </p>
                    <ul style={{ paddingLeft: 20, margin: '0 0 12px', lineHeight: 1.7 }}>
                      <li>Real-time visitor &amp; session tracking</li>
                      <li>Conversion funnel + drop-off detection</li>
                      <li>Customer intelligence dashboards</li>
                      {savedPropId && <li>GA4 historical data for deeper context</li>}
                    </ul>
                    <p style={{ marginTop: 12, fontSize: 13, color: '#6b7280' }}>
                      💡 <strong>Try it now:</strong> open your site in a new tab — you'll show up in your dashboard within seconds.
                    </p>
                    <p style={{ marginTop: 8, fontSize: 13, color: '#6b7280' }}>
                      Smarter pattern-based insights kick in after about <strong>24–72 hours</strong> of traffic.
                    </p>
                  </>
                }
                ctaLabel="Take me to my dashboard"
                onCta={() => {
                  setShowSuccessModal(false);
                  navigate('/dashboard');
                }}
                onClose={() => setShowSuccessModal(false)}
                showBackfill={false}
              />
            </div>
          </div>
        )}
      </Page>
    </div>
  );
}